import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  ChatCircle,
  UsersFour,
  Users,
  Funnel,
  EnvelopeSimple,
  Car,
  Rocket,
  Sparkle,
  Crown,
} from '@phosphor-icons/react';
import api from '@/services/api';

const STEPS = [
  {
    title: 'Welcome to LotMate!',
    desc: 'Your AI-powered dealership CRM. Let\u2019s take a quick tour of the key features.',
    sidebarLabel: null,
    centered: true,
  },
  {
    title: 'Inbox',
    desc: 'All customer conversations in one place. Your AI agent handles responses 24/7 \u2014 SMS, WhatsApp, and more.',
    sidebarLabel: 'Inbox',
  },
  {
    title: 'Pipeline',
    desc: 'See every deal at a glance. Drag cards between stages to track progress from first contact to closed sale.',
    sidebarLabel: 'Pipeline',
  },
  {
    title: 'Contacts',
    desc: 'Your complete customer database with vehicle interest, trade-in details, and AI-collected profile data.',
    sidebarLabel: 'Contacts',
  },
  {
    title: 'Action Center',
    desc: 'Your daily to-do list. See what needs attention \u2014 hot leads, overdue follow-ups, and AI escalations.',
    sidebarLabel: 'Action Center',
  },
  {
    title: 'Inventory',
    desc: 'Live vehicle inventory with lot aging alerts, VIN decode, and automatic matching to customer preferences.',
    sidebarLabel: 'Inventory',
  },
  {
    title: 'AI Coach',
    desc: 'Weekly coaching reports, AI insights, and deal intelligence \u2014 all in one place to improve performance.',
    sidebarLabel: 'AI Coach',
  },
  {
    title: 'Settings',
    desc: 'Configure your dealership profile, team, AI agent name, notifications, and business hours.',
    sidebarLabel: 'Settings',
  },
  {
    title: 'Need help anytime?',
    desc: 'Click the floating LotMate icon in the bottom-right corner to chat with our help bot. It knows everything about the platform!',
    sidebarLabel: null,
    centered: true,
  },
  {
    title: 'You\u2019re all set!',
    desc: 'Start exploring LotMate. You can replay this tour anytime from the Help menu (? icon at top).',
    sidebarLabel: null,
    centered: true,
    isCelebration: true,
  },
];

export function isTourCompleted(user) {
  if (localStorage.getItem('lotmate_tour_completed') === 'true') return true;
  if (user?.tour_completed) return true;
  return false;
}

export function markTourCompleted() {
  localStorage.setItem('lotmate_tour_completed', 'true');
  api.post('/auth/tour-complete').catch(() => {});
}

export function getTourHighlightLabel(step) {
  if (step >= 0 && step < STEPS.length) return STEPS[step].sidebarLabel;
  return null;
}

// ── Sparkle particles for celebration ───────────────────────
function SparkleEffect() {
  const particles = Array.from({ length: 24 }, (_, i) => {
    const left = 5 + Math.random() * 90;
    const delay = Math.random() * 0.8;
    const dur = 1.0 + Math.random() * 1.0;
    const size = 4 + Math.random() * 8;
    const colors = ['#22c55e', '#f59e0b', '#3b82f6', '#a855f7', '#ef4444', '#06b6d4'];
    const color = colors[i % colors.length];
    return (
      <span
        key={i}
        style={{
          position: 'absolute',
          left: `${left}%`,
          top: '-10px',
          width: size,
          height: size,
          borderRadius: '50%',
          background: color,
          animation: `tour-sparkle-fall ${dur}s ${delay}s ease-out forwards`,
          opacity: 0,
          pointerEvents: 'none',
        }}
      />
    );
  });
  return <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>{particles}</div>;
}

// ── Mascot inside card ─────────────────────────────────────
function Mascot({ size, isCelebration }) {
  const [imgFailed, setImgFailed] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {isCelebration && (
        <div className="tour-crown-anim" style={{ marginBottom: 4 }}>
          <Crown size={32} weight="fill" color="#f59e0b" />
        </div>
      )}
      <div
        className="tour-float-anim"
        style={{ width: size, height: size }}
      >
        {imgFailed ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, hsl(var(--primary) / 0.15), hsl(var(--primary) / 0.05))',
              border: '2px solid hsl(var(--primary) / 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: size * 0.25,
              fontWeight: 800,
              color: 'hsl(var(--primary))',
            }}
          >
            LM
          </div>
        ) : (
          <img
            src="/logo.svg"
            alt="LotMate"
            onError={() => setImgFailed(true)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              pointerEvents: 'none',
            }}
          />
        )}
      </div>
    </div>
  );
}

// ── CSS keyframes ──────────────────────────────────────────
const TOUR_STYLES = `
@keyframes tour-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}
@keyframes tour-crown-bounce {
  0% { transform: translateY(0); }
  12% { transform: translateY(-14px); }
  24% { transform: translateY(0); }
  36% { transform: translateY(-9px); }
  48% { transform: translateY(0); }
  60% { transform: translateY(-5px); }
  72% { transform: translateY(0); }
  100% { transform: translateY(0); }
}
@keyframes tour-slide-in {
  from { opacity: 0; transform: translateX(-16px) scale(0.96); }
  to { opacity: 1; transform: translateX(0) scale(1); }
}
@keyframes tour-slide-in-center {
  from { opacity: 0; transform: translateY(12px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes tour-sparkle-fall {
  0% { opacity: 1; transform: translateY(0) scale(1) rotate(0deg); }
  100% { opacity: 0; transform: translateY(200px) scale(0.2) rotate(360deg); }
}
.tour-float-anim {
  animation: tour-float 2.5s ease-in-out infinite;
}
.tour-crown-anim {
  animation: tour-crown-bounce 1.8s ease-in-out;
}
.tour-card-slide {
  animation: tour-slide-in 0.35s ease-out both;
}
.tour-card-slide-center {
  animation: tour-slide-in-center 0.35s ease-out both;
}
`;

export default function GuidedTour({ run, onFinish }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [celebrationDone, setCelebrationDone] = useState(false);
  const [tooltipPos, setTooltipPos] = useState(null);
  const [animKey, setAnimKey] = useState(0);
  const autoDismissRef = useRef(null);

  const measureTarget = useCallback(() => {
    const s = STEPS[step];
    if (!s || s.centered || !s.sidebarLabel) {
      setTooltipPos(null);
      return;
    }
    const el = document.querySelector(`[data-tour="${s.sidebarLabel}"]`);
    if (!el) {
      setTooltipPos(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setTooltipPos({
      top: rect.top + rect.height / 2,
      left: rect.right + 16,
    });
  }, [step]);

  useEffect(() => {
    if (run) {
      setVisible(false);
      setCelebrationDone(false);
      const t = setTimeout(() => {
        measureTarget();
        setAnimKey((k) => k + 1);
        setVisible(true);
      }, 100);
      return () => clearTimeout(t);
    }
  }, [run, step, measureTarget]);

  useEffect(() => {
    if (!run) return;
    const handleResize = () => measureTarget();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [run, measureTarget]);

  useEffect(() => {
    if (run && step === STEPS.length - 1 && visible) {
      autoDismissRef.current = setTimeout(() => {
        setCelebrationDone(true);
        setTimeout(() => {
          setStep(0);
          markTourCompleted();
          onFinish?.();
        }, 300);
      }, 3500);
      return () => clearTimeout(autoDismissRef.current);
    }
  }, [run, step, visible, onFinish]);

  const complete = useCallback(() => {
    if (autoDismissRef.current) clearTimeout(autoDismissRef.current);
    setVisible(false);
    setTimeout(() => {
      setStep(0);
      markTourCompleted();
      onFinish?.();
    }, 250);
  }, [onFinish]);

  const goNext = useCallback(() => {
    setVisible(false);
    setTimeout(() => setStep((s) => s + 1), 200);
  }, []);

  const goBack = useCallback(() => {
    setVisible(false);
    setTimeout(() => setStep((s) => s - 1), 200);
  }, []);

  useEffect(() => {
    if (run) {
      window.__lotmate_tour_step = step;
      window.dispatchEvent(new CustomEvent('lotmate-tour-step', { detail: step }));
    } else {
      window.__lotmate_tour_step = -1;
      window.dispatchEvent(new CustomEvent('lotmate-tour-step', { detail: -1 }));
    }
  }, [run, step]);

  if (!run) return null;

  const isLast = step === STEPS.length - 1;
  const s = STEPS[step];
  const isCentered = s.centered;
  const mascotSize = s.isCelebration ? 160 : 120;

  const tooltipCard = (
    <div
      key={animKey}
      className={isCentered ? 'tour-card-slide-center' : 'tour-card-slide'}
      style={{ opacity: visible ? undefined : 0, position: 'relative' }}
    >
      <div
        className="bg-card border border-border rounded-2xl shadow-2xl relative overflow-hidden"
        style={{ width: isCentered ? 420 : 340 }}
      >
        {s.isCelebration && visible && <SparkleEffect />}

        {/* Gradient accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-primary via-primary/70 to-primary/40" />

        <div className="p-5">
          {/* Close button */}
          <button
            onClick={complete}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground text-lg leading-none transition-colors z-10"
            aria-label="Close tour"
          >
            &times;
          </button>

          {/* Mascot — always inside card, replaces step icons */}
          <div className="flex justify-center mb-3">
            <Mascot size={mascotSize} isCelebration={s.isCelebration} />
          </div>

          {/* Content */}
          <h2 className="text-base font-semibold text-center mb-1">{s.title}</h2>
          <p className="text-xs text-muted-foreground text-center leading-relaxed mb-4">{s.desc}</p>

          {/* Step dots */}
          <div className="flex justify-center gap-1.5 mb-4">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  if (i !== step) {
                    setVisible(false);
                    setTimeout(() => setStep(i), 200);
                  }
                }}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === step
                    ? 'w-5 bg-primary'
                    : 'w-1.5 bg-muted-foreground/25 hover:bg-muted-foreground/40'
                }`}
                aria-label={`Go to step ${i + 1}`}
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-between">
            <button
              onClick={complete}
              className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip tour
            </button>
            <div className="flex gap-2">
              {step > 0 && (
                <button
                  onClick={goBack}
                  className="px-3 py-1 text-xs rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Back
                </button>
              )}
              {isLast ? (
                <button
                  onClick={complete}
                  className="px-3 py-1 text-xs rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-medium transition-colors"
                >
                  Get Started
                </button>
              ) : (
                <button
                  onClick={goNext}
                  className="px-3 py-1 text-xs rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-medium transition-colors"
                >
                  Next
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Arrow pointing to sidebar item */}
      {!isCentered && (
        <div
          style={{
            position: 'absolute',
            left: -8,
            top: '50%',
            marginTop: -8,
            width: 0,
            height: 0,
            borderTop: '8px solid transparent',
            borderBottom: '8px solid transparent',
            borderRight: '8px solid hsl(var(--border))',
          }}
        />
      )}
    </div>
  );

  return (
    <>
      <style>{TOUR_STYLES}</style>

      <div
        onClick={(e) => { if (e.target === e.currentTarget) complete(); }}
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]"
        style={{
          transition: 'opacity 0.25s ease',
          opacity: visible && !celebrationDone ? 1 : 0,
          pointerEvents: visible && !celebrationDone ? 'auto' : 'none',
        }}
      >
        {isCentered ? (
          <div className="flex items-center justify-center h-full">
            {tooltipCard}
          </div>
        ) : (
          tooltipPos && (
            <div
              style={{
                position: 'fixed',
                top: tooltipPos.top,
                left: tooltipPos.left,
                transform: 'translateY(-50%)',
                zIndex: 51,
                transition: 'top 0.35s ease, left 0.35s ease',
              }}
            >
              {tooltipCard}
            </div>
          )
        )}
      </div>
    </>
  );
}
